import { useEffect, useRef } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '@/lib/firebase';
import { useAddDeviceTokenMutation } from '@/store/api/userApi';
import { useAppDispatch, useIsAuthenticated } from './redux';
import { addFcmNotification } from '@/store/slices/notificationSlice';
import { showNotificationToast } from '@/utils/helper';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY; // get from Firebase Console > Cloud Messaging > Web Push certs

// Detect whether the client is Mobile or Desktop
const detectDeviceType = (): 'Mobile' | 'Desktop' => {
  try {
    const ua = navigator.userAgent || '';
    return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'Mobile' : 'Desktop';
  } catch (e) {
    return 'Desktop';
  }
};

// Detect platform: prefer common mobile platforms, otherwise fall back to sensible values
const detectPlatform = (): string => {
  try {
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';

    // Try userAgentData (client hints) if available
    const nav = navigator as any;
    if (nav.userAgentData && nav.userAgentData.platform) return String(nav.userAgentData.platform);

    const platform = navigator.platform || '';
    if (/Win/i.test(platform)) return 'Windows';
    if (/Mac/i.test(platform)) return 'Mac';
    if (/Linux/i.test(platform)) return 'Linux';

    return 'Web';
  } catch (e) {
    return 'Web';
  }
};

export const useNotificationPermission = () => {
  const isAuthenticated = useIsAuthenticated();
  const [addDeviceToken] = useAddDeviceTokenMutation();
  const dispatch = useAppDispatch();
  const requested = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (requested.current || !messaging) return;
    requested.current = true;
    const setup = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        // ── unregister stale SWs first ──────────────────────────────
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));

        // ── re-register fresh ───────────────────────────────────────
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/',
        });

        // wait until the SW is fully active before getting token
        await navigator.serviceWorker.ready;

        const token = await getToken(messaging!, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (token) {
          console.log('FCM Token:', token);
          await addDeviceToken({
            token,
            deviceType: detectDeviceType(),
            platform: detectPlatform(),
          });
        }
        onMessage(messaging!, (payload) => {
          const { title = '', body = '' } = payload.notification ?? {};
          const id = payload.messageId?.replace(/\//g, '_') ?? Date.now().toString();
          showNotificationToast(title, body);
          dispatch(
            addFcmNotification({
              id,
              title,
              body,
              createdAt: new Date().toISOString(),
              read: false,
              source: 'fcm',
            })
          );
        });
      } catch (err) {
        console.error('FCM setup failed:', err);
      }
    };
    setup();
  }, [addDeviceToken, isAuthenticated, dispatch]);
};
``;
